import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import GridTable from "../../../components/GridTable/GridTable";
import endpoints from "../../../services/endpoints";
import { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";
import useApi from "../../../services/useApi";
import { useEffect, useState } from "react";
import Image from "../../../components/images/Image";
import { setPersistedParams, setListOfBankTransactionsUrl } from '../../../store/slices/selectedBankIdSlice';

export default function ListOfBankTransactions({ group_type, group, reports }: { group_type: string; group: string; reports: boolean }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
  const { id } = useParams();
  const { selectedBankId, listOfBankTransactionsUrl, listOfBankAccountsUrl, listOfBalanceSheetUrl } = useSelector((state: RootState) => state.selectedBankId);
  const { getAPI } = useApi();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('end_date')
  const startDate = searchParams.get('start_date')
  const [runningBalance, setRunningBalance] = useState<any>(0);
  const [isError, setIsError] = useState(false);
  const [varience, setVarience] = useState<any>(0);
  const [apiLoading, setApiLoading] = useState(false);
  const { bankTransactions, walletTransactions, recoveryPayableTransactions, customerStockTransactions, serviceFeeExpenseTransactions, equityCapitalTransactions, generalExpenseTransactions, cvmCommissionTransactions } = endpoints;
  const location = useLocation();
  
  // Common field configurations
  const commonFields = {
    transactionId: {
      id: "transaction_id",
    },
    date: {
      id: "created_at",
      custom: ["created_at"],
      className: "min-w-[248px]",
      // sort: true,
      // sortKey: "created_at",
    },
    debit: {
      id: "debit",
      custom: ["debit"],
    },
    credit: {
      id: "credit",
      custom: ["credit"],
    },
    balance: {
      id: "balance",
      custom: ["balance"],
    },
  };

  // Wallet-specific fields
  const walletFields = {
    "Transaction Type": {
      id: "transaction_category",
    },
    "Commission Type": {
      id: "commission_type",
    },
    "Parent": {
      id: "parent",
    },
    "Internal Split": {
      id: "split",
    },
    "External Split": {
      id: "external_split",
    },
  };

  // Wallet-specific fields
  const recoveryWalletFields = {
    "Description": {
      id: "description",
      className: "min-w-[417px]",
    },
    "Split": {
      id: "split",
    },
  };

  // Customer Stock GL specific fields
  const customerStockFields = {
    "Transaction Type": {
      id: "transaction_category",
    },
    "MNO Name": {
      id: "mno_name",
    },
    "Service Fee Type": {
      id: "service_fee_type",
    },
    "Parent": {
      id: "parent",
    },
    "Internal Split": {
      id: "internal_split",
    },
    "External Split": {
      id: "external_split",
    },
  };

  // Etopup Service Fee Expense specific fields
  const etopupServiceFeeExpenseFields = {
    "Transaction Type": {
      id: "transaction_type",
    },
    "MNO Name": {
      id: "mno_name",
    },
    "Phone Number": {
      id: "phone_number",
    },
    "User Name and Code": {
      id: "user",
      custom: ["user"],
      className: "min-w-[200px]",

    },
    "Location": {
      id: "location",
    },
    "Service Fee Type": {
      id: "service_fee_type",
    },
    "Description": {
      id: "description",
      className: "min-w-[417px]",
    },
    "Split": {
      id: "split",
    },
  };

  // Equity Capital specific fields (for Equity/Capital and Capital/Owner Equity)
  const equityCapitalFields = {
    "Description": {
      id: "description",
      className: "min-w-[417px]",
    },
    "Split": {
      id: "split",
    },
  };

  // Non-wallet specific fields
  const regularFields = {
    "Description": {
      id: "description",
      className: "min-w-[417px]",
    },
    "Location": {
      id: "location",
    },
    "Split": {
      id: "split",
    },
  };

  // General Expense specific fields (without Location)
  const generalExpenseFields = {
    "Description": {
      id: "description",
      className: "min-w-[417px]",
    },
    "Split": {
      id: "split",
    },
  };

  // CVM Commission specific fields (with Location)
  const cvmCommissionFields = {
    "Location": {
      id: "location",
    },
    "User Name (User Code)": {
      id: "receiver",
      custom: ["receiver"],
      className: "min-w-[200px]",
    },
    "Service Fee Type": {
      id: "service_fee_type",
    },
    "Description": {
      id: "description",
      className: "min-w-[417px]",
    },
    "Split": {
      id: "split",
    },
  };

  // Single table details configuration with conditional spread
  const getTableDetails = (groupType: string) => ({
    "Transaction ID": commonFields.transactionId,
    "Date and Time": commonFields.date,
    
    // Conditionally include wallet fields if groupType is 'Wallet'
    ...(groupType === 'Wallet' && walletFields),

    // Conditionally include recovery wallet fields if groupType is 'Recovery Payable'
    ... (groupType === 'Recovery Payable' && recoveryWalletFields),
    
    // Conditionally include customer stock fields if groupType is 'Customer Stock GL'
    ...(groupType === 'Customer Stock GL' && customerStockFields),
    
    // Conditionally include service fee expense fields if groupType is 'Etopup Service Fee Expense'
    ...(groupType === 'Etopup Service Fee Expense' && etopupServiceFeeExpenseFields),

    // Conditionally include equity capital fields if groupType is 'Capital/Owner Equity'
    ...(groupType === 'Capital/Owner Equity' && equityCapitalFields),
    
    // Conditionally include general expense fields if groupType is 'General Expense'
    ...(groupType === 'General Expense' && generalExpenseFields),
    
    // Conditionally include CVM Commission fields if groupType is 'CVM Commission'
    ...(groupType === 'CVM Commission' && cvmCommissionFields),
    
    // Conditionally include regular fields if groupType is NOT 'Wallet', 'Recovery Payable', 'Customer Stock GL', 'Etopup Service Fee Expense', 'General Expense', 'CVM Commission', 'Equity/Capital', or 'Capital/Owner Equity'
    ...(groupType !== 'Wallet' && groupType !== 'Recovery Payable' && groupType !== 'Customer Stock GL' && groupType !== 'Etopup Service Fee Expense' && groupType !== 'General Expense' && groupType !== 'CVM Commission' && groupType !== 'Equity/Capital' && groupType !== 'Capital/Owner Equity' && regularFields),
    
    "Debit": commonFields.debit,
    "Credit": commonFields.credit,
    "Balance": commonFields.balance,
  });

  const TableDetails: { [key: string]: any } = getTableDetails(group_type)

  const handleActions = async (rowId: string, key: string, text?: string) => {
    navigate(`/zoodpay-money/balance-sheet/${group_type.replace(/ /g, "-").toLowerCase()}/${rowId}`);
  };

  const staticText = {
    emptyScreenText: {
      heading1: "No Data",
      heading2: "No records found.",
    },
  };
  // Helper to check if listOfBalanceSheetUrl has 'date' as a search param
  const dynamicFilterConfig: DynamicFilterConfig = {
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "start_date",
        end: "end_date",
      },
    },
  };

  // Function to get extra heading text based on group type
  const getExtraHeadingText = (groupType: string): string => {
    switch (groupType) {
      case "Bank Accounts":
        return "";
      case "Cash in Hand":
        return "Regional Office Name";
      case "Account Receivable (Customers) GL":
      case "Wallet":
      case "ZoodPay/Parent Payable":
      case "Service Fee Expense":
        return "User Name (User Code)";
      case "Bundle Activation MNO Inventory GL":
      case "Bundle Activation MNO Payable GL":
      case "Etopup MNO Inventory GL":
      case "Etopup MNO Payable GL":
        return "MNO Name";
      case "Mobile Money":
        return "Money Transfer Platform";
      default:
        return "";
    }
  };

  const extraHeadingText = getExtraHeadingText(group_type);
  const renderSummary = ({ entireResponse, loading }: any) => (
    <div className="mt-3 flex justify-between pr-1 ">
      {loading ? (
        <>
          <div></div>
          <div className="h-6 w-48 animate-pulse bg-gray-200 rounded"></div>
        </>
      ) : (
        <>
          <div></div>
          <div>
            Total Balance{" "}
            {entireResponse?.total_amount !== "-" ? (
              <span className="text-p_primary font-bold">
                AFA{" "}
                {entireResponse?.total_amount?.toString().startsWith("-")
                  ? "(-) " +
                  Math.abs(Number(entireResponse.total_amount)).toFixed(2)
                  : Number(entireResponse?.total_amount).toFixed(2)}
              </span>
            ) : (
              <span className="text-p_primary font-bold ">-</span>
            )}
          </div>
        </>
      )}
    </div>
  );
  const getWalletBallance = async () => {
    setApiLoading(true);
    const response = await getAPI(`mnos/external-wallet/${selectedBankId}`)
    if (!response.error) {
      setRunningBalance(response.data.data.external_wallet_balance)
      setVarience(response.data.data.variance)
      setIsError(false)
    } else {
      setRunningBalance(0)
      setVarience(0)
      setIsError(true)
    }
    setApiLoading(false);
  }
  useEffect(() => {
    dispatch(setPersistedParams({
      searchParams: searchParams.toString(),
      id: id,
      group_type: group_type || null,
      group: group || null,
    }));
  }, [searchParams])
  useEffect(() => {
    dispatch(setListOfBankTransactionsUrl(location.pathname + location.search));
  }, [location.pathname, location.search, dispatch]);
  useEffect(() => {
    if (group_type === "Bundle Activation MNO Inventory GL" || group_type === "Etopup MNO Inventory GL") {
      getWalletBallance()
    }
  }, [])
  const renderExtraHeading = ({ entireResponse, loading }: any) => (
    <div className="flex justify-between items-center bg-white rounded-t-lg px-6 pt-4 pb-2">
      <div>
        <div className="text-heading2">
          {loading ? (
            <span className="h-6 w-24 inline-block animate-pulse bg-gray-200 rounded"></span>
          ) : <>
            <span>{entireResponse?.entity_data?.name
              ? `${entireResponse?.entity_data?.name} ${entireResponse?.entity_data?.user_code
                ? `(${entireResponse?.entity_data?.user_code})`
                : ""
              }`
              : (
                group_type === "Recovery Payable"
                  ? "Recovery Payable"
                  : (
                    group_type === "Customer Stock GL"
                      ? "Customer Stock GL"
                      : (
                        group_type === "Etopup Service Fee Expense"
                          ? "Etopup Service Fee Expense"
                          : (
                            group_type === "General Expense"
                              ? "General Expense"
                              : (
                                group_type === "CVM Commission"
                                  ? "CVM Commission"
                                  : (
                                    group_type === "Capital/Owner Equity"
                                      ? "Capital/Owner Equity"
                                      : "—"
                                  )
                              )
                          )
                      )
                  )
              )}</span>
              
          </>}
        </div>
        <div className="text-subTitle text-[#555555]">{extraHeadingText}</div>
        {(group_type === "Bank Accounts" || group_type === "Bank Charges") && (
          <div className="text-subTitle text-[#555555]">
            Account Number: <span className={loading ? "h-6 w-24 inline-block animate-pulse bg-gray-200 rounded" : ''}>{loading ? '' : entireResponse?.entity_data?.account_number}</span>
          </div>
        )}
      </div>
      <div className="flex gap-12">
        <div className="text-right">
          <div className="text-heading2 text-p_primary">
            {loading ? (
              <span className="h-6 w-24 inline-block animate-pulse bg-gray-200 rounded"></span>
            ) : (
              <>
                {entireResponse?.running_balance === "-"
                  ? "-"
                  : entireResponse?.running_balance?.toString().startsWith("-")
                    ? "AFA (-) " +
                    Math.abs(Number(entireResponse.running_balance)).toFixed(2)
                    : 'AFA ' + Number(entireResponse?.running_balance).toFixed(2)}
              </>
            )}
          </div>
          <div className="text-subTitle text-[#555555]">Running Balance</div>
        </div>
        {group_type === "Bundle Activation MNO Inventory GL" ||
          (group_type === "Etopup MNO Inventory GL" && (
            <div className="text-right">
              <div className="text-heading2 text-p_primary">
                {apiLoading ? (
                  <span className="h-6 w-48 inline-block animate-pulse bg-gray-200 rounded"></span>
                ) : isError ? (
                  <div className="flex items-center gap-2 justify-end mt-[10px]">
                    <Image src="error-connect" className="w-5 h-5" />
                    <span className="text-negative text-body2">
                      Failed to fetch
                    </span>
                  </div>
                ) : (
                  <>
                    {runningBalance === "-"
                      ? "-"
                      : runningBalance?.toString().startsWith("-")
                        ? "AFA (-) " + Math.abs(Number(runningBalance)).toFixed(2)
                        : "AFA " + Number(runningBalance).toFixed(2)}
                  </>
                )}
              </div>
              <div className="text-subTitle text-[#555555]">
                Running Balance through API
              </div>
            </div>
          ))}
        {group_type === "Bundle Activation MNO Inventory GL" ||
          (group_type === "Etopup MNO Inventory GL" && (
            <div className="text-right">
              <div className="text-heading2 text-p_primary">
                {apiLoading ? (
                  <span className="h-6 w-[190px] inline-block animate-pulse bg-gray-200 rounded"></span>
                ) : isError ? (
                  <div className="flex items-center gap-2 justify-end mt-[10px]">
                    <Image src="error-connect" className="w-5 h-5" />
                    <span className="text-negative text-body2">
                      Failed to fetch
                    </span>
                  </div>
                ) : (
                  <>
                    {varience === "-"
                      ? "-"
                      : varience?.toString().startsWith("-")
                        ? "AFA (-) " + Math.abs(Number(varience)).toFixed(2)
                        : "AFA " + Number(varience).toFixed(2)}
                  </>
                )}
              </div>
              <div className="text-subTitle text-[#555555]">Variance</div>
            </div>
          ))}
      </div>
    </div>
  );
  const commonLinks = [
    listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
    listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
    listOfBankAccountsUrl ? listOfBankAccountsUrl : `/zoodpay-money/balance-sheet/${group_type
      .replace(/ /g, "-")
      .toLowerCase()}`,
  ]
  // Mapping for dynamic heading and navPath based on group_type
  const groupTypeConfig: Record<string, { heading: string; navPath: string[], links: string[] }> = {
    "Bank Accounts": {
      heading: "Bank Account Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Bank Accounts",
        "Bank Account Transactions",
      ],
      links: commonLinks
    },
    "Cash in Hand": {
      heading: "Cash in Hand Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Cash in Hand",
        "Cash in Hand Transactions",
      ],
      links: commonLinks
    },
    "Account Receivable (Customers) GL": {
      heading: "Account Receivable (Customers) GL",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Account Receivables (Customers) GL",
        "Account Receivables (Customers) GL Transactions",
      ],
      links: commonLinks
    },
    "Wallet" : {
      heading: `List of Wallet Transactions`,
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        `List of Wallet Transactions`,
      ],
      links: [
          listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
          listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`
        ]
    },
    "Bundle Activation MNO Inventory GL": {
      heading: reports ? "Bundle Activation MNO Inventory GL Transactions" : "Bundle Activation MNO Inventory GL Transactions",
      navPath: reports ? [
        "Reports",
        "MNO Wise eTop Up Available Balance Report",
        "Bundle Activation MNO Inventory GL"
      ] : [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Bundle Activation MNO Inventory GL",
        "Bundle Activation MNO Inventory GL Transactions",
      ],
      links: reports ? [
        `/reports/mno-etopup-available-balance`,
        `/reports/mno-etopup-available-balance`
      ] : commonLinks
    },
    "Etopup MNO Inventory GL": {
      heading: reports ? "Etopup MNO Inventory GL Transactions" : "Etopup MNO Inventory GL Transactions",
      navPath: reports ? [
        "Reports",
        "MNO Wise eTop Up Available Balance Report",
        "Etopup MNO Inventory GL"
      ] : [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Etopup MNO Inventory GL",
        "Etopup MNO Inventory GL Transactions",
      ],
      links: reports ? [
        `/reports/mno-etopup-available-balance`,
        `/reports/mno-etopup-available-balance`
      ] : commonLinks
    },
    "Bank Charges": {
      heading: "Bank Charge Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Bank Charge Accounts",
        "Bank Charge Transactions",
      ],
      links: commonLinks
    },
    "Mobile Money": {
      heading: "Mobile Money Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Mobile Money",
        "Mobile Money Transactions",
      ],
      links: commonLinks
    },
    "Etopup MNO Payable GL": {
      heading: "Etopup MNO Payable GL Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Etopup MNO Payable GL",
        "Etopup MNO Payable GL Transactions",
      ],
      links: commonLinks
    },
    "Bundle Activation MNO Payable GL": {
      heading: "Bundle Activation MNO Payable GL Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Bundle Activation MNO Payable GL",
        "Bundle Activation MNO Payable GL Transactions",
      ],
      links: commonLinks
    },
    "ZoodPay/Parent Payable": {
      heading: "ZoodPay/Parent Payable Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of ZoodPay/Parent Payable",
        "ZoodPay/Parent Payable Transactions",
      ],
      links: commonLinks
    },
    "Recovery Payable": {
      heading: "Recovery Payable Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Recovery Payable Transactions",
      ],
      links: [
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`
      ]
    },
    "Customer Stock GL": {
      heading: "Customer Stock GL Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Customer Stock GL Transactions",
      ],
      links: [
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`
      ]
    },
    "Etopup Service Fee Expense": {
      heading: "Etopup Service Fee Expense Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Etopup Service Fee Expense Transactions",
      ],
      links: [
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`
      ]
    },
    "General Expense": {
      heading: "General Expense Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "General Expense Transactions",
      ],
      links: [
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`
      ]
    },
    "CVM Commission": {
      heading: "List of CVM Commission Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of CVM Commission Transactions",
      ],
      links: [
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`
      ]
    },
    "Bundle Activation Service Fee Revenue": {
      heading: "Bundle Activation Service Fee Revenue Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Bundle Activation Service Fee Revenue",
        "Bundle Activation Service Fee Revenue Transactions",
      ],
      links: commonLinks
    },
    "Etopup Service Fee Revenue": {
      heading: "Etopup Service Fee Revenue Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Etopup Service Fee Revenue",
        "Etopup Service Fee Revenue Transactions",
      ],
      links: commonLinks
    },
    "Capital/Owner Equity": {
      heading: "Capital/Owner Equity Transactions",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Capital/Owner Equity Transactions",
      ],
      links: [
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl ? listOfBalanceSheetUrl : `/zoodpay-money/balance-sheet`
      ]
    }
    // Add more group_types as needed
  };
  const config = groupTypeConfig[group_type] || {
    heading: `${group_type} Transactions`,
    navPath: [
      "Zoodpay Money",
      "Balance Sheet",
      `List of ${group_type}`,
      `${group_type} Transactions`,
    ],
      links: commonLinks
  };
  return (
    <div>
      <Breadcrumbs
        navPath={config.navPath}
        heading={config.heading}
        links={config.links}
        button={reports ? undefined : {
          buttonName: "Add Manual",
          className: "px-4 whitespace-nowrap",
          buttonOnclick: () => {
            // Only navigate for group types that have routes defined
            if (group_type === "Recovery Payable") {
              navigate(`/zoodpay-money/balance-sheet/recovery-payable/transactions/add-manual-entries`)
            } else if (group_type === "Customer Stock GL") {
              navigate(`/zoodpay-money/balance-sheet/customer-stock/transactions/add-manual-entries`)
            } else if (group_type === "Equity/Capital" || group_type === "Capital/Owner Equity") {
              navigate(`/zoodpay-money/balance-sheet/equity-capital/transactions/add-manual-entries`)
            } else if (group_type === "Etopup Service Fee Expense") {
              dispatch(setPersistedParams({ group_type, group }));
              navigate(`/zoodpay-money/balance-sheet/etopup-service-fee-expense/transactions/add-manual-entries`)
            } else if (group_type === "General Expense") {
              dispatch(setPersistedParams({ group_type, group }));
              navigate(`/zoodpay-money/balance-sheet/general-expense/transactions/add-manual-entries`)
            } else if (group_type === "CVM Commission") {
              dispatch(setPersistedParams({ group_type, group }));
              navigate(`/zoodpay-money/balance-sheet/cvm-commission/transactions/add-manual-entries`)
            } else {
              let pathSegment: string;
              if (group_type === "Mobile Money") {
                pathSegment = "mobile-money";
              } else {
                pathSegment = "etopup-mno-inventory-gl";
              }
              navigate(`/zoodpay-money/balance-sheet/${pathSegment}/${id}/add-manual-entries`)
            }
          },
        }}
        extraPadding={true}
      />
      <GridTable
        staticText={staticText}
        mainEndPoint={
          group_type === 'Wallet' 
            ? walletTransactions 
            : group_type === 'Recovery Payable' 
              ? recoveryPayableTransactions 
              : group_type === 'Customer Stock GL'
                ? customerStockTransactions
                : group_type === 'Etopup Service Fee Expense'
                  ? serviceFeeExpenseTransactions
                  : group_type === 'General Expense'
                    ? generalExpenseTransactions
                    : group_type === 'CVM Commission'
                      ? cvmCommissionTransactions
                      : group_type === 'Equity/Capital' || group_type === 'Capital/Owner Equity'
                        ? equityCapitalTransactions
                        : `${bankTransactions}/${id}`
        }
        TableDetails={TableDetails}
        handleActions={(id: string, key: string, text?: string) =>
          handleActions(id, key, text)
        }
        params={(() => {
          const baseParams: { [key: string]: string } = group_type === 'Wallet' || group_type === 'Recovery Payable' || group_type === 'Customer Stock GL' || group_type === 'Etopup Service Fee Expense' || group_type === 'General Expense' || group_type === 'CVM Commission' || group_type === 'Equity/Capital' || group_type === 'Capital/Owner Equity'
            ? { page_number: "1" } 
            : { page_number: "1", group_type: group_type, group: group };
          
          // Include date range parameters if they exist in URL
          if (startDate) {
            baseParams.start_date = startDate;
          }
          if (date) {
            baseParams.end_date = date;
          }
          
          return baseParams;
        })()}
        searchPlaceholder={`Search by transaction id${group_type === 'Etopup Service Fee Expense' || group_type === 'CVM Commission' ? '' : group_type === 'Wallet' ? ', internal split' : group_type === 'Customer Stock GL' ? ', internal split' : group_type === 'Equity/Capital' || group_type === 'Capital/Owner Equity' || group_type === 'General Expense' ? ', split' : ', split'}`}
        filters={true}
        exporting={true}
        reportType="Balance Sheet"
        // Excel export is enabled for all balance sheet lists
        notfoundRoute={{ state: { route: `/zoodpay-money/balance-sheet` } }}
        title={`View Transaction Details`}
        hasViewMDPrivilege={true}
        addMorePadding={true}
        dynamicFilterConfig={dynamicFilterConfig}
        renderSummary={renderSummary}
        renderExtraHeading={renderExtraHeading}
        heightClassName="h-addwithViewHight"
        customHeight="h-[108px]"
        balanceSheet={true}
        mainDivScroll={true}
      customEmptyScreenHeight="h-[16rem] w-[10.5rem] lg:w-[12.5rem] xl:w-[14.5rem]"
        // emptyScreenHeight="h-[350px]"
      />
    </div>
  );
}
